import { without } from '../object';

const capitalize = (str: string) => {
  if (str.trim().length === 0) {
    return str;
  }
  const downcase = str.trim().toLowerCase();
  const firstChar = downcase[0]?.toUpperCase() ?? '';

  return `${firstChar}${downcase.slice(1)}`;
};

interface ErrorTypes {
  error?: string;
  errors?: Record<string, string>[] | Record<string, string[]>;
  message?: string;
}

interface Errors {
  data?: ErrorTypes | string;
  message?: string;
}

// the last block of getErrorMessages will recursively get all strings
// from an error response and add them to the list of errors
// use this to filter out any error messages we don't want to show
const ignoredMessages = [
  'https://tools.ietf.org',
];

export function getErrorMessages({
  error,
  expectedKeys,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: Record<string, any>;
  expectedKeys: string[];
}): string[] {
  const localError = error as Errors;

  try {
    const errors: string[] = [];

    if (localError.data && typeof localError.data === 'string') {
      errors.push(localError.data);

      return errors;
    }

    if (localError.message) {
      errors.push(localError.message);

      return errors;
    }

    if (localError.data && typeof localError.data !== 'string') {
      const errorMessages = localError.data?.errors;
      const errorMessage = (localError.data?.message) ??
        (localError.data?.error);

      if (errorMessages && Array.isArray(errorMessages)) { // rails api
        errorMessages.forEach(err => {
          const message = err.title ?? err.error;

          if (message) {
            errors.push(message);
          }
        });
      } else if (errorMessages) {
        const unexpectedErrors = without(errorMessages, expectedKeys);
        Object.values(unexpectedErrors).forEach(values => {
          ((values ?? []) as string[]).forEach(value => {
            errors.push(value);
          });
        });
      } else if (errorMessage) {
        errors.push(errorMessage);
      } else if (localError.data &&
        typeof localError.data === 'object' &&
        Object.keys(localError.data).length > 0) { // handling default errors from Rails microservices
        Object.entries(localError.data).forEach(([k, v]: [string, Record<string, unknown> | string | string[]]) => {
          if (Array.isArray(v)) {
            v.forEach(message => {
              if (!ignoredMessages.some(ignoredMessage => message.startsWith(ignoredMessage))) {
                errors.push(`${capitalize(k)} - ${capitalize(message)}`);
              }
            });
          } else if (typeof v === 'string' && !ignoredMessages.some(ignoredMessage => v.startsWith(ignoredMessage))) {
            errors.push(v);
          }
        });
      } else {
        errors.push('Something went wrong.');
      }
    }

    return errors;
  } catch (_err) {
    return ['Something went wrong.'];
  }
}
